import { useState } from "react";
import { Info } from "lucide-react";
import { useT } from "@/lib/i18n";
import { track } from "@/lib/analytics";

const KEY = "fev.howItWorks.collapsed";
const GRID_ROWS = ["off_grid", "weak_grid", "on_grid"];

const readCollapsed = () => {
  try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
};
const writeCollapsed = (v) => {
  try { localStorage.setItem(KEY, v ? "1" : "0"); } catch { /* storage blocked */ }
};

const Block = ({ title, children }) => (
  <div>
    <div className="font-display text-base mb-2" style={{ color: "var(--charcoal)" }}>{title}</div>
    <div className="text-sm leading-relaxed space-y-2" style={{ color: "var(--body)" }}>{children}</div>
  </div>
);

const GridRow = ({ id, highlight, t }) => (
  <li
    data-testid={`how-it-works-grid-${id}`}
    className="pl-3 py-1"
    style={{ borderLeft: highlight ? "3px solid var(--brand-green)" : "3px solid transparent" }}
  >
    <strong>{t(`how.b3.${id}.t`)}</strong> — {t(`how.b3.${id}.d`)}
    {highlight && (
      <span className="ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold" data-testid="how-it-works-yours"
            style={{ background: "rgba(26,111,43,0.1)", color: "var(--brand-green)" }}>
        {t("how.b3.yours")}
      </span>
    )}
  </li>
);

const Body = ({ gridStatus, t }) => (
  <>
    <p className="text-sm leading-relaxed" style={{ color: "var(--body)" }}>{t("how.summary")}</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
      <Block title={t("how.b1.title")}><p>{t("how.b1.body")}</p></Block>
      <Block title={t("how.b2.title")}><p>{t("how.b2.body")}</p></Block>
      <Block title={t("how.b3.title")}>
        <p>{t("how.b3.intro")}</p>
        <ul className="space-y-1">
          {GRID_ROWS.map((id) => <GridRow key={id} id={id} highlight={gridStatus === id} t={t} />)}
          <GridRow id="none" highlight={false} t={t} />
        </ul>
      </Block>
      <Block title={t("how.b4.title")}><p>{t("how.b4.body")}</p></Block>
    </div>
    <div className="mt-6">
      <Block title={t("how.use.title")}><p>{t("how.use.body")}</p></Block>
    </div>
    <p className="text-xs mt-4" style={{ color: "var(--muted)" }}>{t("how.scope")}</p>
  </>
);

// Prompt L — the "How it works" explainer: a panel on the wizard, body-only in the Results dialog.
export const HowItWorks = ({ gridStatus, variant = "panel" }) => {
  const t = useT();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  if (variant === "content") {
    return <div data-testid="how-it-works"><Body gridStatus={gridStatus} t={t} /></div>;
  }
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(next);
    if (!next) track("how_it_works_open", { surface: "wizard" });
  };
  return (
    <section className="card p-5 md:p-6 mt-6" data-testid="how-it-works">
      <div className="flex items-center gap-2">
        <Info size={16} style={{ color: "var(--brand-green)" }} />
        <h2 className="font-display text-lg flex-1" style={{ color: "var(--charcoal)" }}>{t("how.title")}</h2>
        <button type="button" onClick={toggle} className="text-sm link" data-testid="how-it-works-toggle"
                aria-expanded={!collapsed}>
          {collapsed ? t("how.show") : t("how.hide")}
        </button>
      </div>
      <div className="mt-3">
        {collapsed
          ? <p className="text-sm leading-relaxed" style={{ color: "var(--body)" }}>{t("how.summary")}</p>
          : <Body gridStatus={gridStatus} t={t} />}
      </div>
    </section>
  );
};
